import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";
import {
    IGNITE_CONSULT_URL,
    PAPERFORM_URL,
    SHORT_FORM_VIDEO_URL,
    PLATFORM_DEMO_VIDEO_URL,
} from "@/lib/leads";

const PROBLEMS = [
    {
        n: "01",
        t: "Benefits decisions made without enough information.",
    },
    {
        n: "02",
        t: "Vendors that don't fit how the workforce actually operates.",
    },
    {
        n: "03",
        t: "Rollouts that stall because no one owns the path forward.",
    },
];

export default function Home() {
    return (
        <div data-testid="page-home">
            {/* Hero */}
            <section className="section pt-16 md:pt-24" data-testid="hero">
                <div className="container-x">
                    <div className="max-w-4xl space-y-7">
                        <p className="eyebrow">Talent &amp; benefits advisory</p>
                        <h1 className="h1">
                            A clearer path to the right HR and{" "}
                            <span className="text-navy">benefits solutions.</span>
                        </h1>
                        <p className="lead max-w-2xl">
                            HR Connect helps employers evaluate their benefits
                            options and makes a direct, warm introduction to
                            Ignite Health — a benefits program covering
                            telemedicine, mental health support, prescription
                            coverage, and preventive care, at no out-of-pocket
                            cost to employees.
                        </p>
                        <div className="flex flex-col gap-4 items-start pt-2">
                            <a
                                href="#video"
                                data-testid="hero-primary-cta"
                                className="group inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow"
                            >
                                <PlayCircle className="h-5 w-5" strokeWidth={1.75} />
                                Watch the 2-Minute Overview ↓
                            </a>
                            <a
                                href={IGNITE_CONSULT_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="hero-supporting-cta"
                                className="text-sm text-ink-soft hover:text-brand transition-colors"
                            >
                                Already know what you need?{" "}
                                <span className="underline underline-offset-4 decoration-border">
                                    Book a consult directly with Ignite Health
                                </span>{" "}
                                →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Who HR Connect Is */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="who-we-are"
            >
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-4">
                            An advisory brand, not an affiliate page.
                        </p>
                        <h2 className="h2">Who HR Connect is.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="lead">
                            We look at what your organization actually needs,
                            confirm fit, and make a direct introduction to
                            Ignite Health — no cold referral, no vendor
                            runaround. Ignite Health handles everything from
                            there.
                        </p>
                    </div>
                </div>
            </section>

            {/* What We Help Solve */}
            <section className="section" data-testid="what-we-solve">
                <div className="container-x">
                    <div className="max-w-2xl mb-14">
                        <p className="eyebrow mb-4">
                            Three problems we hear constantly.
                        </p>
                        <h2 className="h2">What we help solve.</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-px bg-border">
                        {PROBLEMS.map((p) => (
                            <div
                                key={p.n}
                                className="bg-background p-8 md:p-10 space-y-5"
                                data-testid={`problem-${p.n}`}
                            >
                                <p className="font-heading text-5xl text-navy">
                                    {p.n}
                                </p>
                                <p className="body">{p.t}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Partner: Ignite Health */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="featured-partner"
            >
                <div className="container-x">
                    <div className="max-w-3xl mb-12">
                        <p className="eyebrow mb-4">Our current featured offer.</p>
                        <h2 className="h2">
                            The HR Connect{" "}
                            <span className="text-navy">×</span> Ignite Health.
                        </h2>
                        <p className="lead mt-5">
                            Our flagship offer right now connects eligible employers
                            with Ignite Health for a custom benefits proposal. It's
                            our most-referenced path, and it's actively running.
                        </p>
                    </div>
                </div>
            </section>

            {/* PRIMARY video block */}
            <section
                id="video"
                className="section pt-0"
                data-testid="home-video"
            >
                <div className="container-x max-w-4xl">
                    <div className="max-w-2xl mb-10">
                        <p className="eyebrow mb-4">Overview</p>
                        <h2 className="h2">See how it works in 2 minutes.</h2>
                        <p className="lead mt-5">
                            A quick look at what Ignite Health offers and whether
                            it's a fit for your team.
                        </p>
                    </div>

                    <div
                        className="aspect-video w-full border border-border bg-black overflow-hidden mb-10"
                        data-testid="home-video-main"
                    >
                        <video
                            className="w-full h-full"
                            controls
                            playsInline
                            preload="metadata"
                            data-testid="home-video-primary-player"
                        >
                            <source src={SHORT_FORM_VIDEO_URL} type="video/mp4" />
                            Your browser doesn't support embedded video.
                        </video>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                        <a
                            href={PAPERFORM_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="home-video-primary-cta"
                            className="group inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow"
                        >
                            Get Your Free Benefits Assessment
                            <ArrowRight
                                className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                                strokeWidth={1.75}
                            />
                        </a>
                    </div>
                </div>
            </section>

            {/* SECONDARY video block — optional platform demo */}
            <section
                className="pb-16 md:pb-24 pt-4"
                data-testid="home-video-secondary"
            >
                <div className="container-x max-w-3xl">
                    <div className="mb-6">
                        <p className="eyebrow mb-2 text-ink-soft/80">
                            Want to see the platform itself?
                        </p>
                        <h3 className="font-heading text-2xl md:text-3xl font-semibold text-navy">
                            A short look inside Ignite Health.
                        </h3>
                    </div>
                    <div
                        className="aspect-video w-full border border-border bg-black overflow-hidden"
                        data-testid="home-video-secondary-player-wrap"
                    >
                        <video
                            className="w-full h-full"
                            controls
                            playsInline
                            preload="metadata"
                            data-testid="home-video-demo-player"
                        >
                            <source src={PLATFORM_DEMO_VIDEO_URL} type="video/mp4" />
                            Your browser doesn't support embedded video.
                        </video>
                    </div>
                </div>
            </section>

            {/* Where We're Headed */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="where-headed"
            >
                <div className="container-x grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <p className="eyebrow mb-4">Where we're headed</p>
                        <h2 className="h2">Focused on Ignite Health, for now.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="lead">
                            Right now, we do one thing: connect eligible
                            employers with Ignite Health. It's the offer we know
                            best and stand behind fully.
                        </p>
                    </div>
                </div>
            </section>

            {/* Get Started */}
            <section
                className="section border-t border-border"
                data-testid="get-started"
            >
                <div className="container-x max-w-3xl text-center">
                    <p className="eyebrow mb-4">Get started</p>
                    <h2 className="h2">
                        Let's figure out the right{" "}
                        <span className="text-navy">next step together.</span>
                    </h2>
                    <p className="lead mt-5">
                        Watch the overview or go straight to your free benefits
                        assessment — we'll take it from there.
                    </p>
                    <div className="mt-10 flex flex-col items-center gap-4">
                        <a
                            href={PAPERFORM_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="get-started-primary-cta"
                            className="group inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow"
                        >
                            Get Your Free Benefits Assessment
                            <ArrowRight
                                className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                                strokeWidth={1.75}
                            />
                        </a>
                        <a
                            href={IGNITE_CONSULT_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="get-started-supporting-cta"
                            className="text-sm text-ink-soft hover:text-brand transition-colors"
                        >
                            Or{" "}
                            <span className="underline underline-offset-4 decoration-border">
                                book a consult directly with Ignite Health
                            </span>{" "}
                            →
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
